import {
  TableCell,
  TableCellProps,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import fiSchema from "../../translations/fi.json";

export type SortOrder = "asc" | "desc";

type TranslationKey<T extends keyof typeof fiSchema> = keyof typeof fiSchema[T];

export type TableHeader<T> = {
  transLationKey: TranslationKey<"file-explorer">;
  sortKey?: keyof T;
  tableCellProps?: TableCellProps;
};

type FileExplorerTableHeadProps<T> = {
  setSortBy: (sortBy: keyof T) => void;
  setOrder: (sort: SortOrder | ((pevVar: SortOrder) => SortOrder)) => void;
  tableHeaders: TableHeader<T>[];
  sortBy: keyof T;
  order: SortOrder;
};

export const FileExplorerTableHead = <T,>({
  setSortBy,
  setOrder,
  tableHeaders,
  sortBy,
  order,
}: FileExplorerTableHeadProps<T>) => {
  const { t } = useTranslation("file-explorer");
  const handleSort = React.useCallback(
    (sortKey: keyof T) => {
      setSortBy(sortKey);
      const sortOrder = order === "asc" ? "desc" : "asc";
      setOrder(sortOrder);
    },
    [order, setOrder, setSortBy],
  );

  const tableHead = React.useMemo(() => {
    return tableHeaders.map((v) => {
      const headContent =
        v.sortKey === undefined ? (
          t(v.transLationKey)
        ) : (
          <TableSortLabel
            active={v.sortKey === sortBy}
            direction={v.sortKey === sortBy ? order : "asc"}
            onClick={() => handleSort(v.sortKey as keyof T)}
          >
            {t(v.transLationKey)}
          </TableSortLabel>
        );
      return (
        <TableCell key={v.transLationKey} {...v.tableCellProps}>
          {headContent}
        </TableCell>
      );
    });
  }, [handleSort, order, sortBy, t, tableHeaders]);

  return (
    <TableHead
      sx={{
        backgroundColor: "#e6e6e6",
        "& th": {
          backgroundColor: "#e6e6e6",
        },
      }}
    >
      <TableRow>{tableHead}</TableRow>
    </TableHead>
  );
};
